<template>
  <q-dialog
    :model-value="modelValue"
    :persistent="persistent"
    data-test-id="update-access-level-dialog"
    @update:model-value="$emit('update:model-value', $event)"
  >
    <q-card
      class="relative-position"
      bordered
      flat
    >
      <q-card-section>
        <div class="row items-center no-wrap">
          <div class="col-auto">
            <q-btn
              v-if="!persistent"
              v-close-popup
              icon="close"
              flat
              round
              dense
            />
          </div>
          <div class="col text-h6 q-pl-md">
            Update Access Level for {{ user.firstName }} <span class="sensitive-data">{{ user.lastName }}</span>
          </div>
        </div>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="text-subtitle2 q-pl-md">
          Access Level
        </div>
        <q-list>
          <!-- Admin -->
          <q-item
            v-if="$isPermitted($permissions.Security.PromoteUserToAdminOrEmployee)"
            clickable
            :disable="isSavingChanges"
            @click="newAccessLevel = ACCESS_LEVELS.ADMIN"
          >
            <q-item-section avatar>
              <q-radio
                v-model="newAccessLevel"
                class="no-pointer-events"
                val="admin"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-subtitle1">
                {{ newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES ? 'Circuit' : 'Site' }} Admin
              </q-item-label>
              <q-item-label class="text-grey-9">
                Has access to all admin and employee apps for {{ newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES ? 'all of the current circuit\'s Sites' : 'the following authorized Sites only' }}.
                <br>The user is treated as a Non-Employee on unauthorized sites.
              </q-item-label>
            </q-item-section>
          </q-item>

          <!-- Employee -->
          <q-item
            v-if="$isAnyPermitted($permissions.Security.PromoteUserToEmployee, $permissions.Security.PromoteUserToAdminOrEmployee)"
            clickable
            :disable="isSavingChanges"
            @click="newAccessLevel = ACCESS_LEVELS.EMPLOYEE"
          >
            <q-item-section avatar>
              <q-radio
                v-model="newAccessLevel"
                class="no-pointer-events"
                val="employee"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-subtitle1">
                {{ newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES ? 'Circuit' : 'Site' }} Employee
              </q-item-label>
              <q-item-label class="text-grey-9">
                Has access to employee apps, including Point of Sale and Kitchen Display System, for {{ newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES ? 'all of the current circuit\'s Sites' : 'the following authorized Sites only' }}.
                <br>The user does not have access to the admin app.
                <br>The user is treated as a Non-Employee on unauthorized sites.
              </q-item-label>
            </q-item-section>
          </q-item>

          <!-- Non-Employee -->
          <q-item
            v-if="!hideNonEmployeeOption && $isAnyPermitted($permissions.Security.PromoteUserToEmployee, $permissions.Security.PromoteUserToAdminOrEmployee)"
            clickable
            :disable="isSavingChanges"
            @click="newAccessLevel = ACCESS_LEVELS.NON_EMPLOYEE"
          >
            <q-item-section avatar>
              <q-radio
                v-model="newAccessLevel"
                class="no-pointer-events"
                val="non-employee"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-subtitle1">
                Non-Employee
              </q-item-label>
              <q-item-label class="text-grey-9">
                Does not have access to any admin or employee apps.
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <!-- Authorized Sites -->
        <template v-if="newAccessLevel === ACCESS_LEVELS.ADMIN || newAccessLevel === ACCESS_LEVELS.EMPLOYEE">
          <div class="text-subtitle2 q-mt-md q-pl-md">
            Authorized Sites
          </div>
          <q-list>
            <q-item
              clickable
              :disable="isSavingChanges"
              @click="newAuthorizedSitesScope = AUTHORIZED_SITES_SCOPES.SOME_CURRENT_CIRCUIT_SITES"
            >
              <q-item-section avatar>
                <q-radio
                  v-model="newAuthorizedSitesScope"
                  class="no-pointer-events"
                  :val="AUTHORIZED_SITES_SCOPES.SOME_CURRENT_CIRCUIT_SITES"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-subtitle1">
                  Specific <em>{{ currentCircuit.name }}</em> Sites <span class="text-caption text-grey-8">(site level)</span>
                </q-item-label>
                <q-item-label>
                  The user will have {{ newAccessLevelLabel }} access to selected <em>{{ currentCircuit.name }}</em> sites.
                </q-item-label>
                <q-select
                  v-if="newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.SOME_CURRENT_CIRCUIT_SITES"
                  ref="authorizedSitesSelect"
                  v-model="newAuthorizedSitesOfCurrentCircuit"
                  class="q-mt-sm"
                  label="Authorized Sites"
                  :options="currentUser.libraryAndAuthorizedSiteOptions"
                  map-options
                  option-label="name"
                  option-value="id"
                  multiple
                  use-chips
                  stack-label
                  bottom-slots
                  empty-label="No sites available"
                  :rules="[val => val.length > 0 || 'Please select at least one site']"
                />
              </q-item-section>
            </q-item>
            <q-item
              v-if="currentUser.circuitAuthorized"
              clickable
              :disable="isSavingChanges"
              @click="newAuthorizedSitesScope = AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES"
            >
              <q-item-section avatar>
                <q-radio
                  v-model="newAuthorizedSitesScope"
                  class="no-pointer-events"
                  :val="AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-subtitle1">
                  All <em>{{ currentCircuit.name }}</em> Sites <span class="text-caption text-grey-8">(circuit level)</span>
                </q-item-label>
                <q-item-label class="text-grey-9">
                  The user will have {{ newAccessLevelLabel }} access to all <strong>current and future</strong>&nbsp;<em>{{ currentCircuit.name }}</em> sites.
                </q-item-label>
                <q-item-label v-if="newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES">
                  <q-chip
                    v-for="site in currentUser.libraryAndAuthorizedSiteOptions"
                    :key="site.id"
                    dense
                  >
                    {{ site.name }}
                  </q-chip>
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </template>
      </q-card-section>

      <q-card-section
        v-if="autoAssignedRoles.length"
        class="q-pt-none"
      >
        <div class="text-subtitle2 q-pl-md">
          Automatically Assigned Roles
        </div>
        <div class="q-pl-md text-grey-9">
          <q-chip
            v-for="role in autoAssignedRoles"
            :key="role.id"
            dense
          >
            {{ role.name }}
          </q-chip>
        </div>
      </q-card-section>

      <q-card-section
        v-if="newAccessLevel !== ACCESS_LEVELS.NON_EMPLOYEE"
        class="q-pt-none"
      >
        <div class="text-subtitle2 q-pl-md">
          Password Reset
        </div>
        <q-list>
          <q-item
            clickable
            :disable="isSavingChanges"
            @click="sendResetPasswordEmail = !sendResetPasswordEmail"
          >
            <q-item-section avatar>
              <q-checkbox
                v-model="sendResetPasswordEmail"
                class="no-pointer-events"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label class="text-subtitle1">
                Send password reset email
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>

      <q-card-actions
        class="q-pb-md q-pr-md"
        align="right"
      >
        <q-btn
          color="primary"
          :loading="isSavingChanges"
          :outline="!hasUnsavedChanges"
          :disable="!hasUnsavedChanges"
          @click="updateAccessLevel"
        >
          Save
        </q-btn>
      </q-card-actions>
      <q-inner-loading :showing="isSavingChanges" />
    </q-card>
  </q-dialog>
</template>

<script>
const ACCESS_LEVELS = Object.freeze({
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
  NON_EMPLOYEE: 'non-employee'
})

const AUTHORIZED_SITES_SCOPES = Object.freeze({
  ALL_CURRENT_CIRCUIT_SITES: 'all-current-circuit-sites',
  SOME_CURRENT_CIRCUIT_SITES: 'some-current-circuit-sites'
})

export default {
  name: 'UpdateAccessLevelDialog',
  props: {
    user: {
      type: Object,
      required: true
    },
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    hideNonEmployeeOption: {
      type: Boolean,
      required: false,
      default: false
    },
    persistent: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'save'],
  data () {
    return {
      currentAccessLevel: null,
      currentAuthorizedSitesScope: null,
      currentAuthorizedSitesOfCurrentCircuit: [],
      newAccessLevel: null,
      newAuthorizedSitesOfCurrentCircuit: [],
      newAuthorizedSitesScope: null,
      sendResetPasswordEmail: true,
      isSavingChanges: false
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit'],
  models: {
    roles: {
      variables () {
        return {
          autoAssignable: true,
          skip: !this.value
        }
      }
    }
  },
  computed: {
    autoAssignedRoles () {
      if (this.newAccessLevel === ACCESS_LEVELS.ADMIN) {
        return _.filter(this.roles.data, 'autoAssignAdmin')
      } else if (this.newAccessLevel === ACCESS_LEVELS.EMPLOYEE) {
        return _.filter(this.roles.data, 'autoAssignEmployee')
      } else {
        return []
      }
    },
    hasUnsavedChanges () {
      return this.willChangeAccessLevel || this.willChangeAuthorizedSitesScope || this.willChangeAuthorizedSites
    },
    willChangeAccessLevel () {
      return this.newAccessLevel !== this.currentAccessLevel
    },
    willChangeAuthorizedSitesScope () {
      return this.newAuthorizedSitesScope !== this.currentAuthorizedSitesScope
    },
    willChangeAuthorizedSites () {
      const currentAuthorizedSiteIds = _.sortBy(_.map(this.currentAuthorizedSitesOfCurrentCircuit, 'id'), 'id')
      const newAuthorizedSiteIds = _.sortBy(_.map(this.newAuthorizedSitesOfCurrentCircuit, 'id'), 'id')
      return !_.isEqual(currentAuthorizedSiteIds, newAuthorizedSiteIds)
    },
    newAccessLevelLabel () {
      switch (this.newAccessLevel) {
        case ACCESS_LEVELS.ADMIN:
          return 'Admin'
        case ACCESS_LEVELS.EMPLOYEE:
          return 'Employee'
        default:
          return 'Non-Employee'
      }
    }
  },
  watch: {
    modelValue (newValue) {
      // Dialog shown
      if (newValue) {
        this.reset()
      }
    }
  },
  created () {
    _.extend(this, { ACCESS_LEVELS, AUTHORIZED_SITES_SCOPES })
  },
  methods: {
    close () {
      this.$emit('update:model-value', false)
    },
    // Using data variables to track changes instead of computed props so the "current" values don't
    // unexpectedly change if the user model is saved/fetched.
    reset () {
      this.sendResetPasswordEmail = true
      this.isSavingChanges = false

      if (this.user.admin) {
        this.currentAccessLevel = ACCESS_LEVELS.ADMIN
      } else if (this.user.employee) {
        this.currentAccessLevel = ACCESS_LEVELS.EMPLOYEE
      } else {
        this.currentAccessLevel = ACCESS_LEVELS.NON_EMPLOYEE
      }
      this.newAccessLevel = this.currentAccessLevel

      if (this.user.circuitAuthorized) {
        this.currentAuthorizedSitesScope = AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES
      } else {
        this.currentAuthorizedSitesScope = AUTHORIZED_SITES_SCOPES.SOME_CURRENT_CIRCUIT_SITES
      }
      this.newAuthorizedSitesScope = this.currentAuthorizedSitesScope

      this.currentAuthorizedSitesOfCurrentCircuit = _(this.user.authorizedSites?.data)
        .filter(site => site.circuitId === this.currentCircuit.id)
        .sortBy('name')
        .value()
      this.newAuthorizedSitesOfCurrentCircuit = _.clone(this.currentAuthorizedSitesOfCurrentCircuit)
    },
    async updateAccessLevel () {
      if (!this.hasUnsavedChanges) return

      if (this.newAccessLevel !== ACCESS_LEVELS.NON_EMPLOYEE && this.newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.SOME_CURRENT_CIRCUIT_SITES) {
        if (!this.$refs.authorizedSitesSelect.validate()) return
      }

      this.isSavingChanges = true

      if (this.currentAccessLevel !== ACCESS_LEVELS.NON_EMPLOYEE && this.newAccessLevel === ACCESS_LEVELS.NON_EMPLOYEE) {
        const success = await this.demoteUser()
        if (success) {
          // Regular users cannot have other access options so we can return early if successful
          this.isSavingChanges = false
          this.$emit('save')
          this.close()
          return
        }
      }

      // If demoting from admin to employee, first demote the user. Afterward we can use the promote mutation
      // to update authorized sites if necessary.
      if (this.currentAccessLevel === ACCESS_LEVELS.ADMIN && this.newAccessLevel === ACCESS_LEVELS.EMPLOYEE) {
        const success = await this.demoteUser()
        if (!success) {
          this.isSavingChanges = false
          return
        }
      }

      // At this point, we are either promoting a user to employee or admin, updating authorized sites, or both.
      // We can use the promote mutation to handle all of these cases.
      const success = await this.promoteUser()
      if (success) {
        this.$emit('save')
        this.close()
      }
      this.isSavingChanges = false
    },
    async promoteUser () {
      let success = false
      let errors = null
      const newAuthorizedSiteIds = _.map(this.newAuthorizedSitesOfCurrentCircuit, 'id')

      try {
        const result = await this.user.promoteAccessLevel({
          makeEmployee: this.newAccessLevel === ACCESS_LEVELS.EMPLOYEE,
          makeAdmin: this.newAccessLevel === ACCESS_LEVELS.ADMIN,
          makeCircuitLevel: this.newAuthorizedSitesScope === AUTHORIZED_SITES_SCOPES.ALL_CURRENT_CIRCUIT_SITES,
          newAuthorizedSiteIds,
          sendResetPasswordEmail: this.sendResetPasswordEmail
        })
        success = result.data.success
        errors = result.errors
      } catch (error) {
        errors = [error.message]
      }

      if (errors?.length) {
        this.$mh.showErrors(errors)
      }
      return success
    },
    async demoteUser () {
      let success = false
      let errors = null

      try {
        const result = await this.user.demoteAccessLevel({
          makeEmployee: this.newAccessLevel === ACCESS_LEVELS.EMPLOYEE,
          makeNonEmployee: this.newAccessLevel === ACCESS_LEVELS.NON_EMPLOYEE
        })
        success = result.data.success
        errors = result.errors
      } catch (error) {
        errors = [error.message]
      }

      if (errors?.length) {
        this.$mh.showErrors(errors)
      }
      return success
    }
  }
}
</script>
